import { isEmpty } from 'lodash';
import { CategoryLabels, CategoryTags } from './dictionaries.helper';
import { AssetDetails, AssetFeatures } from '../types/interfaces';
import { ValuationDataModel } from '../types/valuation.interface';
import {
  AgeData,
  FinancialDataModel,
  HistogramDataModel,
  HistogramIndicatorsBundle,  
  MeanHistogramIndicatorsBundle,
  OfferDemandDataModel,
  ZoneData,
  ZoneDataModel,
  ZonePopulationDataModel,
} from '../types/indicators.interfaces';
import { BoundaryResponse } from '../types/indicators.interfaces';
import { ValuationIndicatorsBundle } from '../types/indicators.interfaces';
import { AssetProfileDataModel } from '../types/assetProfile.interface';
import { Asset, ComparablesDataModel } from '../types/comparables.interfaces';

export const numberFormat = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 2,
  style: 'decimal',
  useGrouping: true,
});

export function getDatesRange(propertyType?: number): string {
  const currentDate = new Date();
  const pastDate = new Date(currentDate);

  if (propertyType && [1, 2, 4, 7].includes(propertyType)) {
    pastDate.setMonth(pastDate.getMonth() - 6); // 6 months before
  } else {
    pastDate.setMonth(pastDate.getMonth() - 12); // 12 months before
  }

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return `${formatDate(pastDate)},${formatDate(currentDate)}`;
}

export function getDistance(propertyType?: number): number {
  if (propertyType && [1, 2, 4, 7].includes(propertyType)) {
    return 1000;
  } else {
    return 3000;
  }
}

export const splitArrayIntoChunks = (
  array: Array<Asset> | null,
  chunkSize: number
): Array<Array<Asset>> | null => {
  let result: Array<Array<Asset>> = [];

  if (!array || array.length === 0) return null;

  // First chunk has `chunkSize - 3` items
  const firstChunkSize = chunkSize - 3;

  // If the array is smaller than or equal to firstChunkSize, return the entire array in one chunk
  if (array.length <= firstChunkSize) {
    result.push(array);
    return result;
  }

  // Push the first chunk
  result.push(array.slice(0, firstChunkSize));

  // Push the remaining chunks of size `chunkSize`
  for (let i = firstChunkSize; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
};

export const getColorByComparisonType = (comparisonType: number): string => {
  switch (comparisonType) {
    case 1:
      return '#EFDDE3'; // Color for comparison_type 1
    case 0:
      return '#D7ECEA'; // Color for comparison_type 0
    case 2:
      return '#F2DBC8'; // Color for comparison_type 2
    default:
      return '#FFFFFF'; // Default color (in case the comparison_type is not recognized)
  }
};

export function getPeriodCodes() {
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth(); // 0-based index (0 = January, 11 = December)
  const currentQuarter = Math.floor(currentMonth / 3) + 1;

  let periods = [];

  // Calculate the current period and the two previous periods
  for (let i = 0; i < 3; i++) {
    // Loop 3 times to get the last 3 periods
    let year = currentYear;
    let quarter = currentQuarter - i;

    if (quarter <= 0) {
      quarter += 4;
      year -= 1;
    }

    periods.unshift(`${year}Q${quarter}`);
  }

  return periods.join(',');
}

/** Países LATAM (código en minúscula) para las adaptaciones que solo aplican allí. */
const LATAM_COUNTRIES = ['mx', 'co', 'pe'];

export const isLatamCountry = (country?: string | null): boolean =>
  LATAM_COUNTRIES.includes(country?.toLowerCase() ?? '');

type BoundaryAdminName =
  | 'admin1_name'
  | 'admin2_name'
  | 'admin3_name'
  | 'admin4_name';

/** Admins que se muestran en cada país LATAM, de lo más específico a lo más general. */
const LATAM_LOCATION_ADMINS: Record<string, BoundaryAdminName[]> = {
  pe: ['admin4_name', 'admin3_name', 'admin1_name'], // distrito, provincia, departamento
  mx: ['admin4_name', 'admin3_name', 'admin2_name'], // localidad, municipio, estado
  co: ['admin3_name', 'admin1_name'], // municipio, departamento
};

/** Nombre de ubicación sin tildes, sin espacios de más y en minúsculas. */
const normalizeLocationName = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

/**
 * Nombres de los admins a mostrar en la ubicación del activo para LATAM, sin los vacíos
 * ni los repetidos (sin distinguir mayúsculas, tildes ni espacios); se conserva la
 * primera aparición. Fuera de LATAM devuelve null y se usan neighborhood, district y city.
 */
export const getLatamLocationAdmins = (
  boundary: Partial<BoundaryResponse> | null | undefined,
  country?: string | null
): string[] | null => {
  if (!isLatamCountry(country)) return null;
  const admins = LATAM_LOCATION_ADMINS[country!.toLowerCase()];
  const seen = new Set<string>();
  return admins
    .map((admin) => boundary?.[admin]?.trim())
    .filter((name): name is string => {
      if (!name) return false;
      const key = normalizeLocationName(name);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const mountAssetProfileData = (
  initialAssetDetails: AssetDetails,
  initialAssetFeatures: Partial<AssetFeatures>,
  boundary: BoundaryResponse,
  country?: string | null
): AssetProfileDataModel => {
  return {
    lat: initialAssetDetails.lat,
    lon: initialAssetDetails.lon,
    address: initialAssetDetails.address,
    propertyType: initialAssetDetails.propertyType ?? null,
    listingType: initialAssetDetails.listingType ?? null,
    area: initialAssetDetails.area ?? null,
    parcelArea: initialAssetDetails.parcelArea ?? null,
    floor: initialAssetDetails.floor ?? null,
    rooms: initialAssetDetails.rooms ?? null,
    nbaths: initialAssetDetails.nbaths ?? null,
    yearBuilt: initialAssetDetails.yearBuilt ?? null,
    buildStatus: initialAssetDetails.buildStatus ?? null,
    features: {
      has_air_conditioner: initialAssetFeatures.has_air_conditioner ?? null,
      has_garage: initialAssetFeatures.has_garage ?? null,
      has_elevator: initialAssetFeatures.has_elevator ?? null,
      has_storage: initialAssetFeatures.has_storage ?? null,
      has_pool: initialAssetFeatures.has_pool ?? null,
      has_terrace: initialAssetFeatures.has_terrace ?? null,
      is_exterior: initialAssetFeatures.is_exterior ?? null,
      has_automatic_door: initialAssetFeatures.has_automatic_door ?? null,
      has_people: initialAssetFeatures.has_people ?? null,
      has_security: initialAssetFeatures.has_security ?? null,
      has_pavement: initialAssetFeatures.has_pavement ?? null,
      has_water: initialAssetFeatures.has_water ?? null,
      has_sewerage: initialAssetFeatures.has_sewerage ?? null,
      has_street_lighting: initialAssetFeatures.has_street_lighting ?? null,
      has_electricity: initialAssetFeatures.has_electricity ?? null,
      has_natural_gas: initialAssetFeatures.has_natural_gas ?? null,
    },
    neighborhood: boundary.admin5_name ?? null,
    district: boundary.admin4_name ?? null,
    city: boundary.admin3_name ?? null,
    locationAdmins: getLatamLocationAdmins(boundary, country),
    cadastralRef: initialAssetDetails.cadastralRef ?? null,
  };
};
/**
 * El €/m² de un informe personalizado llega en la URL (estimated_price_sqm), pero no
 * todas las apps que generan la URL lo envían. Cuando falta o no es válido se deriva
 * del precio total y la superficie, igual que se hace con sale_value_sqm.
 */
export const resolveEstimatedPriceSqm = (
  estimatedPrice: number | null | undefined,
  estimatedPriceSqm: number | null | undefined,
  area: number | null | undefined
): number | null => {
  if (estimatedPriceSqm != null && !isNaN(estimatedPriceSqm) && estimatedPriceSqm > 0)
    return estimatedPriceSqm;
  if (
    estimatedPrice == null ||
    isNaN(estimatedPrice) ||
    estimatedPrice <= 0 ||
    area == null ||
    isNaN(area) ||
    area <= 0
  )
    return null;
  return Math.round(estimatedPrice / area);
};

type PreparedValuationIndicators = {
  neighborhoodYieldRental: number | null;
  districtYieldRental: number | null;
  municipalityYieldRental: number | null;
  timeOnMarket: number | null;
  negotiationLeeway: number | null;
};
export const mountValuationData = (
  saleData: any,
  rentData: any,
  indicators: ValuationIndicatorsBundle[],
  operation: number | null,
  excludeRent: boolean,
  excludeSale: boolean,
  neighborhoodId: number,
  districtId: number,
  municipalityId: number,
  reportType: number,
  estimatedPrice: number | null,
  estimatedPriceSqm: number | null,
  estimatedMin: number | null,
  estimatedMax: number | null,
  listingType?: number,
  social_financial?: any,
  rentValuationFailed?: boolean,
  saleValuationFailed?: boolean,
  yieldParam?: number,
  rentValueParam?: number,
  saleValueParam?: number,
  rentValueSqmParam?: number,
  saleValueSqmParam?: number,
  area?: number
): ValuationDataModel => {
  let preparedIndicators: PreparedValuationIndicators = {
    neighborhoodYieldRental: null,
    districtYieldRental: null,
    municipalityYieldRental: null,
    timeOnMarket: null,
    negotiationLeeway: null,
  };
  if (!isEmpty(indicators)) {
    preparedIndicators.neighborhoodYieldRental =
      indicators.filter(
        (bundle) =>
          bundle.boundary_id == neighborhoodId && bundle.operation == 1
      )[0]?.yield_rental ?? null;
    preparedIndicators.districtYieldRental =
      indicators.filter(
        (bundle) => bundle.boundary_id == districtId && bundle.operation == 1
      )[0]?.yield_rental ?? null;
    preparedIndicators.municipalityYieldRental =
      indicators.filter(
        (bundle) =>
          bundle.boundary_id == municipalityId && bundle.operation == 1
      )[0]?.yield_rental ?? null;
    const boundaryFiltered = indicators.filter(
      (bundle) => bundle.boundary_id == neighborhoodId
    );
    preparedIndicators.timeOnMarket =
      boundaryFiltered[0]?.time_on_market ?? null;
    if (
      saleData &&
      saleData.asset.stats &&
      saleData.asset.stats.area &&
      saleData.asset.stats.area.output_list_to_sale
    ) {
      preparedIndicators.negotiationLeeway =
        saleData.asset.stats.area.output_list_to_sale * 100;
    } else {
      preparedIndicators.negotiationLeeway = null;
    }
  }

  let valuationRentValue: number | null = null;
  let valuationRentValueSqm: number | null = null;
  let valuationSaleValue: number | null = null;
  let valuationSaleValueSqm: number | null = null;

  const calculateValuationValues = (data: any, isRent: boolean, operationFailed?: boolean) => {
    // If the operation failed, return null values instead of estimated values
    if (operationFailed) return { value: null, valueSqm: null };
    if (!data) return { value: estimatedPrice, valueSqm: estimatedPriceSqm };
    return {
      value: Number(data.best_value.toFixed(0)),
      valueSqm: Number((data.best_value / data.asset.area).toFixed(0)),
    };
  };

  if ((reportType === 1 || reportType === 3) && (listingType !== 8 && listingType !== 12)) {
    // Operación alquiler con yield: rent desde valoración AVM (API); venta desde valores calculados en URL
    const isRentWithYield =
      operation === 0 &&
      yieldParam != null &&
      yieldParam > 0 &&
      saleValueParam != null;
    if (isRentWithYield) {
      const rentValues = calculateValuationValues(rentData, true, rentValuationFailed);
      valuationRentValue = rentValues.value;
      valuationRentValueSqm = rentValues.valueSqm;
      valuationSaleValue = saleValueParam;
      valuationSaleValueSqm =
        saleValueSqmParam != null && !isNaN(saleValueSqmParam)
          ? saleValueSqmParam
          : area != null && area > 0
            ? Math.round(saleValueParam / area)
            : null;
    } else {
      const rentValues = calculateValuationValues(rentData, true, rentValuationFailed);
      const saleValues = calculateValuationValues(saleData, false, saleValuationFailed);
      valuationRentValue = rentValues.value;
      valuationRentValueSqm = rentValues.valueSqm;
      valuationSaleValue = saleValues.value;
      valuationSaleValueSqm = saleValues.valueSqm;
    }
  } else if (reportType === 2 || reportType === 3) {
    if (operation === 1) {
      valuationSaleValue = estimatedPrice;
      valuationSaleValueSqm = estimatedPriceSqm;
      const rentValues = calculateValuationValues(rentData, true, rentValuationFailed);
      valuationRentValue = rentValues.value;
      valuationRentValueSqm = rentValues.valueSqm;
    } else if (operation === 0) {
      valuationRentValue = estimatedPrice;
      valuationRentValueSqm = estimatedPriceSqm;
      // Personalized report with yield: use sale_value (and sale_value_sqm) from URL params; otherwise from valuation
      if (yieldParam != null && saleValueParam != null) {
        valuationSaleValue = saleValueParam;
        valuationSaleValueSqm =
          saleValueSqmParam != null && !isNaN(saleValueSqmParam)
            ? saleValueSqmParam
            : area != null && area > 0
              ? Math.round(saleValueParam / area)
              : null;
      } else {
        const saleValues = calculateValuationValues(saleData, false, saleValuationFailed);
        valuationSaleValue = saleValues.value;
        valuationSaleValueSqm = saleValues.valueSqm;
      }
    }
  }

  const salePriceRange =
    saleData &&
    saleData.price_range &&
    Object.keys(saleData.price_range).length > 0
      ? [
          saleData.price_range.min
            ? Number(saleData.price_range.min?.toFixed(0))
            : null,
          saleData.price_range.max
            ? Number(saleData.price_range.max?.toFixed(0))
            : null,
        ]
      : null;

  const rentPriceRange =
    rentData &&
    rentData.price_range &&
    Object.keys(rentData.price_range).length > 0
      ? [
          rentData.price_range.min
            ? Number(rentData.price_range.min?.toFixed(0))
            : null,
          rentData.price_range.max
            ? Number(rentData.price_range.max?.toFixed(0))
            : null,
        ]
      : null;

  const salePriceByStatus =
    saleData && saleData.price_by_status
      ? Object.keys(saleData.price_by_status).length > 0
        ? saleData.price_by_status
        : null
      : null;

  const rentPriceByStatus =
    rentData && rentData.price_by_status
      ? Object.keys(rentData.price_by_status).length > 0
        ? rentData.price_by_status
        : null
      : null;

  const hasYieldWithRental = yieldParam != null && yieldParam > 0;
  const hasRentValueFromParams =
    (rentValueParam != null && !isNaN(rentValueParam)) ||
    (rentValueSqmParam != null && !isNaN(rentValueSqmParam));
  const hasSaleValueFromParams =
    (saleValueParam != null && !isNaN(saleValueParam)) ||
    (saleValueSqmParam != null && !isNaN(saleValueSqmParam));

  return {
    excludeRent: excludeRent ? true : false,
    excludeSale: excludeSale ? true : false,
    reportOperation: Number(operation),
    reportType: reportType,
    listingType: listingType,
    social_financial: social_financial,
    ...(hasYieldWithRental && { yieldParam }),
    rent: {
      bestMethod: hasRentValueFromParams ? null : rentData?.best_method ?? null,
      valueOverridden: hasRentValueFromParams,
      mainValue: valuationRentValue !== null ? Number(valuationRentValue) : 0,
      estimatedMin: estimatedMin,
      estimatedMax: estimatedMax,
      sqmPrice: valuationRentValueSqm !== null ? Number(valuationRentValueSqm) : 0,
      priceRange: (rentPriceRange as [number, number] | null) || null,
      priceByStatus: rentPriceByStatus || null,
      yieldRental: {
        neighborhood: preparedIndicators.neighborhoodYieldRental ?? null,
        district: preparedIndicators.districtYieldRental ?? null,
        municipality: preparedIndicators.municipalityYieldRental ?? null,
      },
    },
    sale: {
      bestMethod: hasSaleValueFromParams ? null : saleData?.best_method ?? null,
      valueOverridden: hasSaleValueFromParams,
      mainValue: valuationSaleValue !== null ? Number(valuationSaleValue) : 0,
      estimatedMin: estimatedMin,
      estimatedMax: estimatedMax,
      sqmPrice: valuationSaleValueSqm !== null ? Number(valuationSaleValueSqm) : 0,
      priceRange: (salePriceRange as [number, number] | null) || null,
      priceByStatus: salePriceByStatus || null,
      timeOnMarket: preparedIndicators.timeOnMarket ?? null,
      negotiationLeeway: preparedIndicators.negotiationLeeway
        ? Number(Math.abs(preparedIndicators.negotiationLeeway).toFixed(1))
        : null,
    },
    rentValuationFailed: rentValuationFailed ?? false,
    saleValuationFailed: saleValuationFailed ?? false,
  };
};

export const mountHistogramData = (
  data: HistogramIndicatorsBundle[] | MeanHistogramIndicatorsBundle[],
  operation: number,
  adminIds: {
    admin1: number;
    admin2: number;
    admin3: number;
    admin4: number;
    admin5: number;
  } | null,
  assetValueSqm: number,
  assetValue: number,
  listingType?: number
): HistogramDataModel => {
  if (isEmpty(data) || !adminIds) {
    return {
      operation: operation,
      assetValue: listingType === 7 || listingType === 11 ? assetValue: assetValueSqm,
      averagePrice: null,
      buckets: null,
    };
  }

  // Helper function to check if an object has valid histogram data
  const hasValidHistogramData = (
    bundle: HistogramIndicatorsBundle | MeanHistogramIndicatorsBundle
  ): boolean => {
    const result = listingType === 7 || listingType === 11
      ? 'output_pricemean' in bundle && bundle.output_pricemean !== null
      : 'output_priceunit' in bundle && bundle.output_priceunit !== null;
    
    // console.log('hasValidHistogramData result:', {
    //   listingType,
    //   bundle,
    //   result,
    //   hasPriceMean: 'output_pricemean' in bundle,
    //   hasPriceUnit: 'output_priceunit' in bundle,
    // });
    
    return result;
  };

  // Try to find a matching bundle with valid data, starting from admin5
  let matchingBundle = null;
  const adminLevels = [
    { id: adminIds.admin5, level: 'admin5' },
    { id: adminIds.admin4, level: 'admin4' },
    { id: adminIds.admin3, level: 'admin3' },
    { id: adminIds.admin2, level: 'admin2' },
    { id: adminIds.admin1, level: 'admin1' },
  ];

  for (const { id } of adminLevels) {
    const bundle = data.find(
      (b) => b.boundary_id === id && b.operation === operation
    );

    if (bundle && hasValidHistogramData(bundle)) {
      matchingBundle = bundle;
      break;
    }
  }

  if (!matchingBundle) {
    return {
      operation: operation,
      assetValue: listingType === 7 || listingType === 11 ? assetValue: assetValueSqm,
      averagePrice: null,
      buckets: null,
    };
  }

  // Extract bucket values and create the histogram data
  const prefix = listingType === 7 || listingType === 11 ? 'output_pricemean' : 'output_priceunit';
  const buckets = Array.from({ length: 10 }, (_, i) => ({
    bucket: i,
    units:
      (matchingBundle as any)[
        `${prefix}_histogram_bucket${i}_units_perc`
      ] || null,
    value:
      (matchingBundle as any)[`${prefix}_histogram_bucket${i}`] || null,
  }));

  return {
    operation: operation,
    assetValue: listingType === 7 || listingType === 11 ? assetValue: assetValueSqm,
    averagePrice: listingType === 7 || listingType === 11 
      ? (matchingBundle as MeanHistogramIndicatorsBundle).output_pricemean 
      : (matchingBundle as HistogramIndicatorsBundle).output_priceunit,
    buckets: buckets,
  };
};

export const mountComparablesData = (
  comparables: Asset[] | null,
  lat: number,
  lon: number
): ComparablesDataModel => {
  if (!comparables) {
    return {
      lat: lat,
      lng: lon,
      comparables: null,
      similarComparables: null,
    };
  }
  const getComparableColor = (publishingStatus: number) => {
    if (publishingStatus == 2) {
      return '#F2DBC8';
    } else if (publishingStatus == 0) {
      return '#EFDDE3';
    } else if (publishingStatus == 1) {
      return '#D7ECEA';
    } else {
      return '#F2DBC8';
    }
  };
  comparables.forEach((item: Asset, index) => {
    item.original_id = item.id || 0;
    item._id = item._id || '';
    item.id = index + 1;
    item.color = getComparableColor(item.comparison_type);
    item.assetFeatures = {
      has_garage: item.has_garage,
      has_elevator: item.has_elevator,
      has_pool: item.has_pool,
      has_air_conditioner: item.has_air_conditioner,
      has_storage: item.has_storage,
      has_terrace: item.has_terrace,
      is_exterior: item.is_exterior,
      has_automatic_door: item.has_automatic_door,
      has_people: item.has_people,
      has_security: item.has_security,
      has_pavement: item.has_pavement,
      has_water: item.has_water,
      has_sewerage: item.has_sewerage,
      has_street_lighting: item.has_street_lighting,
      has_electricity: item.has_electricity,
      has_natural_gas: item.has_natural_gas,
    };
  });

  return {
    lat: lat,
    lng: lon,
    comparables: comparables,
    similarComparables: null,
  };
};

export const mountFinancialData = (
  data: Array<any>,
  operation: number,
  typology_type: number
): FinancialDataModel => {
  if (isEmpty(data)) {
    return {
      operation: operation,
      social_financial: null,
      evolution: null,
    };
  }
  try {
    const countryData = data.find(
      (bundle) =>
        bundle.boundary_type === 10 && bundle.typology_type === typology_type
    );
    const cityData = data.find(
      (bundle) =>
        bundle.boundary_type === 13 && bundle.typology_type === typology_type
    );
    const neighborhoodData = data.find(
      (bundle) =>
        bundle.boundary_type === 15 && bundle.typology_type === typology_type
    );

    const avgRent = data.find(
      (bundle) => bundle.boundary_type === 15 && bundle.typology_type === 1
    )?.income_household_gross;

    let affordability = null;

    if (typology_type === 1 || typology_type === 2 || typology_type === 5) {
      affordability = data.find(
        (bundle) =>
          bundle.boundary_type === 15 &&
          bundle.typology_type === typology_type &&
          bundle.construction_type === 2
      )?.md_affordability_years;
    } else {
      affordability = data.find(
        (bundle) =>
          bundle.boundary_type === 15 &&
          bundle.typology_type === typology_type &&
          bundle.construction_type === 0
      )?.md_affordability_years;
    }

    const unemployment = data.find(
      (bundle) => bundle.boundary_type === 15 && bundle.typology_type === 1
    )?.perc_unemployment;

    const parsedData = {
      operation: operation,
      evolution: [
        {
          boundary: 10,
          value: countryData?.output_priceunit_yoy ?? null,
          valueSqm: countryData?.output_priceunit ?? null,
        },
        {
          boundary: 14,
          value: cityData?.output_priceunit_yoy ?? null,
          valueSqm: cityData?.output_priceunit ?? null,
        },
        {
          boundary: 15,
          value: neighborhoodData?.output_priceunit_yoy ?? null,
          valueSqm: neighborhoodData?.output_priceunit ?? null,
        },
      ],
      social_financial: [
        {
          id: 'averageRent',
          value: avgRent ?? null,
          labelId: 'byHouse',
        },
        {
          id: 'affordability',
          labelId: 'years',
          value: affordability ?? null,
        },
        {
          id: 'unemployment',
          value: unemployment ?? null,
          labelId: 'inZone',
        },
      ],
    };
    return parsedData;
  } catch (error) {
    console.error('Error mounting financial data:', error);
    return {
      operation: operation,
      social_financial: null,
      evolution: null,
    };
  }
};

export const mountOfferDemandData = (
  data: Array<any>,
  selectedOperation: number
): OfferDemandDataModel | null => {
  if (isEmpty(data)) {
    return null;
  }
  const bundle = data.filter(
    (bundle) => bundle.operation == selectedOperation && bundle.category_id == 0
  )[0];
  const selectedOperationIndicators = data.filter(
    (bundle) =>
      bundle.operation == selectedOperation && bundle.category_id !== 0
  );

  const stockUnits = bundle ? bundle.stock_units : null;
  const outputUnits = bundle ? bundle.output_units : null;
  const meanHousingAgeCat0 = bundle ? bundle.mean_housing_age : null;

  const saleBundle = data.filter(
    (bundle) => bundle.category_id == 0 && bundle.operation == 1
  )[0];
  const saleOU = saleBundle ? saleBundle.stock_units : null;
  const rentBundle = data.filter(
    (bundle) => bundle.category_id == 0 && bundle.operation == 0
  )[0];
  const rentOU = rentBundle ? rentBundle.stock_units : null;

  const totalOU = saleOU !== null && rentOU !== null ? saleOU + rentOU : null;

  const salePercDemand = totalOU ? (saleOU * 100) / totalOU : null;
  const rentPercDemand = totalOU ? (rentOU * 100) / totalOU : null;

  const formattedBundle = selectedOperationIndicators
    ? selectedOperationIndicators.map((bundle) => {
        return {
          category: bundle.category_id ?? null,
          outputUnits: bundle.output_units ?? null,
          stockUnits: bundle.stock_units ?? null,
        };
      })
    : null;

  const categories = formattedBundle
    ? formattedBundle.map((bundle) => {
        return {
          category: bundle.category,
          demand: !isNaN(Number((bundle.outputUnits * 100) / outputUnits))
            ? Number((bundle.outputUnits * 100) / outputUnits)
            : null,
          offer: !isNaN(Number((bundle.stockUnits * 100) / stockUnits))
            ? Number((bundle.stockUnits * 100) / stockUnits)
            : null,
          range: CategoryLabels[bundle.category as keyof typeof CategoryLabels],
          tag: CategoryTags[bundle.category as keyof typeof CategoryTags],
        };
      })
    : null;

  const mostDemandedHouse = formattedBundle
    ? formattedBundle.filter(
        (bundle) =>
          bundle.outputUnits ==
          Math.max(...formattedBundle.map((bundle) => bundle.outputUnits))
      )
    : null;

  const currentYear = new Date().getFullYear();
  const meanHousingAge = currentYear - meanHousingAgeCat0;

  return {
    mean_housing_age: meanHousingAge || null, // en años
    most_demanded_house: {
      range:
        mostDemandedHouse && mostDemandedHouse[0]?.category
          ? CategoryLabels[
              mostDemandedHouse[0].category as keyof typeof CategoryLabels
            ]
          : null,
      tag:
        mostDemandedHouse && mostDemandedHouse[0]?.category
          ? CategoryTags[
              mostDemandedHouse[0].category as keyof typeof CategoryTags
            ]
          : null,
    },
    offer_by_purpose: {
      sale: salePercDemand || null,
      rent: rentPercDemand || null,
    },
    categories: categories
      ? categories.map((cat) => ({
          ...cat,
          demand: isNaN(Number(cat.demand)) ? null : Number(cat.demand),
          offer: isNaN(Number(cat.offer)) ? null : Number(cat.offer),
        }))
      : null,
  };
};

export const mountZonePopulationData = (
  data: AgeData
): ZonePopulationDataModel | null => {
  if (isEmpty(data)) {
    return null;
  }

  // Check if there's at least one valid nationality value
  const hasValidNationality = [
    data.perc_national_local,
    data.perc_national_europe,
    data.perc_national_asia,
    data.perc_national_africa,
    data.perc_national_oceania,
    data.perc_national_america,
  ].some((value) => value !== null && value !== 0);

  // Check if there's at least one valid education value
  const hasValidEducation = [
    data.perc_edu_level1,
    data.perc_edu_level2,
    data.perc_edu_level3,
  ].some((value) => value !== null && value !== 0);

  // Check if there's at least one valid age value
  const hasValidAge = [
    data.perc_age_15_19,
    data.perc_age_20_24,
    data.perc_age_25_29,
    data.perc_age_30_34,
    data.perc_age_35_39,
    data.perc_age_40_44,
    data.perc_age_45_49,
    data.perc_age_50_54,
    data.perc_age_55_59,
    data.perc_age_60_64,
    data.perc_age_65_69,
    data.perc_age_70_74,
    data.perc_age_75_79,
    data.perc_age_80_84,
    data.perc_age_85_89,
    data.perc_age_90_94,
    data.perc_age_95_99,
    data.perc_age_100_m,
  ].some((value) => value !== null && value !== 0);

  // Return null if no valid data in any category
  if (!hasValidNationality && !hasValidEducation && !hasValidAge) {
    return null;
  }

  const ageValues = [
    (data.perc_age_15_19 ?? 0) + (data.perc_age_20_24 ?? 0), // Group 1: 15-24 years
    (data.perc_age_25_29 ?? 0) + (data.perc_age_30_34 ?? 0), // Group 2: 25-34 years
    (data.perc_age_35_39 ?? 0) + (data.perc_age_40_44 ?? 0), // Group 3: 35-44 years
    (data.perc_age_45_49 ?? 0) + (data.perc_age_50_54 ?? 0), // Group 4: 45-54 years
    (data.perc_age_55_59 ?? 0) + (data.perc_age_60_64 ?? 0), // Group 5: 55-64 years
    (data.perc_age_65_69 ?? 0) + (data.perc_age_70_74 ?? 0), // Group 6: 65-74 years
    // Group 7: 75+ years (75-79 + 80-84 + 85-89 + 90-94 + 95-99 + 100+)
    (data.perc_age_75_79 ?? 0) +
      (data.perc_age_80_84 ?? 0) +
      (data.perc_age_85_89 ?? 0) +
      (data.perc_age_90_94 ?? 0) +
      (data.perc_age_95_99 ?? 0) +
      (data.perc_age_100_m ?? 0),
  ];
  const foreignPercentage = 100 - (data.perc_national_local ?? 0);
  return {
    nationality: {
      national: data.perc_national_local ?? 0, // en %
      europe:
        data.perc_national_europe === null
          ? 0
          : (data.perc_national_europe * foreignPercentage) / 100,
      asia:
        data.perc_national_asia === null
          ? 0
          : (data.perc_national_asia * foreignPercentage) / 100,
      africa:
        data.perc_national_africa === null
          ? 0
          : (data.perc_national_africa * foreignPercentage) / 100,
      oceania:
        data.perc_national_oceania === null
          ? 0
          : (data.perc_national_oceania * foreignPercentage) / 100,
      america:
        data.perc_national_america === null
          ? 0
          : (data.perc_national_america * foreignPercentage) / 100,
    },
    mean_age: {
      value:
        data.mean_population_age === null ? null : data.mean_population_age,
      ranges: [
        {
          range: '15-25',
          value: ageValues[0], // en %
        },
        {
          range: '25-35',
          value: ageValues[1], // en %
        },
        {
          range: '35-45',
          value: ageValues[2], // en %
        },
        {
          range: '45-55',
          value: ageValues[3], // en %
        },
        {
          range: '55-65',
          value: ageValues[4], // en %
        },
        {
          range: '65-75',
          value: ageValues[5], // en %
        },
        {
          range: '75-85',
          value: ageValues[6], // en %
        },
        {
          range: '>85',
          value: ageValues[7], // en %
        },
      ],
    },
    study_level: {
      value:
        data.perc_edu_level3 === null ? null : Math.round(data.perc_edu_level3), // en %
      categories: {
        college: data.perc_edu_level3 === null ? 0 : data.perc_edu_level3, // en %
        secondary: data.perc_edu_level2 === null ? 0 : data.perc_edu_level2,
        primary: data.perc_edu_level1 === null ? 0 : data.perc_edu_level1,
      },
    },
  };
};

export const mountZoneData = (
  lat: number,
  lon: number,
  data: ZoneData
): ZoneDataModel | null => {
  if (isEmpty(data)) {
    return null;
  }
  return {
    coordinates: [lat, lon],
    ratings: [
      {
        id: 'education',
        value:
          data.perc_access_educational_uni !== null
            ? (data.perc_access_educational_uni / 100) * 5
            : null,
      },
      {
        id: 'health',
        value:
          data.perc_access_healthcare !== null
            ? (data.perc_access_healthcare / 100) * 5
            : null,
      },
      {
        id: 'supermarkets',
        value:
          data.perc_access_retail !== null
            ? (data.perc_access_retail / 100) * 5
            : null,
      },
      {
        id: 'catering',
        value:
          data.perc_access_catering !== null
            ? (data.perc_access_catering / 100) * 5
            : null,
      },
      {
        id: 'public_transport',
        value:
          data.perc_access_transportation !== null
            ? (data.perc_access_transportation / 100) * 5
            : null,
      },
      {
        id: 'offer_leisure__culture',
        value:
          data.perc_access_leisure !== null
            ? (data.perc_access_leisure / 100) * 5
            : null,
      },
      {
        id: 'green_areas',
        value:
          data.perc_access_greenarea !== null
            ? (data.perc_access_greenarea / 100) * 5
            : null,
      },
      {
        id: 'sports',
        value:
          data.perc_access_sports !== null
            ? (data.perc_access_sports / 100) * 5
            : null,
      },
    ],
  };
};

/** Tramos de planta del catálogo de notariado, en minúsculas como se normalizan. */
const FLOOR_CATEGORIES = ['baja', 'intermedia', 'alta'];

/**
 * Clave de traducción del tramo de planta que aporta notariado, o null si no aplica.
 *
 * La fuente no da la planta exacta sino uno de tres tramos (`cat_floor`), así que este dato
 * solo se usa cuando el comparable no trae `floor`.
 */
export const getFloorCategoryKey = (catFloor?: string | null): string | null => {
  const category = catFloor?.trim().toLowerCase();

  return category && FLOOR_CATEGORIES.includes(category) ? `catFloor.${category}` : null;
};

/** El backend manda esta fuente como "Notariado transactions" en `retail_listing`. */
export const isNotariadoSource = (retailListing?: string | null): boolean =>
  retailListing?.trim().toLowerCase() === 'notariado transactions';

/**
 * Etiqueta corta de la fuente del comparable para pintar en el informe.
 *
 * El resto de fuentes (portales inmobiliarios) se muestran tal cual llegan.
 */
export const getRetailListingLabel = (
  retailListing?: string | null
): string | null => {
  if (!retailListing) return null;

  return isNotariadoSource(retailListing) ? 'Notariado' : retailListing;
};
